/* Financial eligibility stays in Liquid. Revalidate only this presentation before its stored data expires. */
if (!customElements.get('cpp-website-offer')) {
  const guardAsset = document.currentScript?.src ? new URL(document.currentScript.src, window.location.href) : null;
  customElements.define('cpp-website-offer', class extends HTMLElement {
    connectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.pageReady = document.readyState === 'complete';
      this.checkExpiry = () => {
        clearTimeout(this.expiryTimer);
        const dates = this.offerDates(this);
        if (!dates || dates.until <= Date.now()) { this.removeOffer(); return; }
        const remaining = dates.until - Date.now();
        const visible = document.visibilityState === 'visible';
        if (this.pageReady && visible && remaining <= 15000 && this.attemptedExpiry !== dates.expiry && !this.flight) {
          this.attemptedExpiry = dates.expiry;
          void this.revalidate(dates.expiry);
        }
        const delay = visible && remaining > 15000 ? remaining - 15000 : remaining;
        this.expiryTimer = setTimeout(this.checkExpiry, Math.min(Math.max(1, delay), 300000));
      };
      this.onPageReady = () => { this.pageReady = true; this.checkExpiry(); };
      window.addEventListener('load', this.onPageReady);
      window.addEventListener('pageshow', this.checkExpiry);
      document.addEventListener('visibilitychange', this.checkExpiry);
      this.checkExpiry();
    }
    offerDates(element) {
      const iso = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/;
      const expiry = element.dataset.expiresAt;
      const end = element.dataset.endsAt;
      if (!iso.test(expiry || '') || (end && !iso.test(end))) return null;
      const until = Math.min(Date.parse(expiry), end ? Date.parse(end) : Infinity);
      return Number.isFinite(until) ? { expiry, until } : null;
    }
    async revalidate(expiry) {
      const sectionId = this.dataset.sectionId;
      const section = this.closest('[id^="shopify-section-"]');
      if (!/^[A-Za-z0-9_-]{1,200}$/.test(sectionId || '') || section?.id !== `shopify-section-${sectionId}`) return;
      let rendererURL;
      try { rendererURL = new URL(this.dataset.rendererUrl, window.location.href); } catch { return; }
      if (!guardAsset || !/\/cpp-website-offer\.js$/.test(guardAsset.pathname) || !/^https?:$/.test(rendererURL.protocol) || rendererURL.origin !== guardAsset.origin || rendererURL.pathname !== guardAsset.pathname.replace(/cpp-website-offer\.js$/, 'section-renderer.js') || rendererURL.username || rendererURL.password || rendererURL.hash || [...rendererURL.searchParams.keys()].length !== 1 || !/^[A-Za-z0-9_-]{1,128}$/.test(rendererURL.searchParams.get('v') || '')) return;
      const flight = { controller: new AbortController(), generation: this.generation, expiry };
      this.flight = flight;
      const stopped = new Promise((_, reject) => { flight.cancel = () => reject(new Error('OFFER_REFRESH_STOPPED')); });
      const timeout = setTimeout(() => { flight.controller.abort(); flight.cancel(); }, 5000);
      flight.timeout = timeout;
      const current = () => this.isConnected && this.generation === flight.generation && this.flight === flight && !flight.controller.signal.aborted && this.dataset.expiresAt === flight.expiry;
      try {
        // No section request or helper import during initial rendering.
        const { sectionRenderer } = await Promise.race([import(rendererURL.href), stopped]);
        if (!current()) return;
        const url = new URL(window.location.href);
        if (!/^https?:$/.test(url.protocol) || url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
        const html = await Promise.race([sectionRenderer.getSectionHTML(sectionId, false, url, { signal: flight.controller.signal, cache: 'no-store', redirect: 'error', credentials: 'same-origin' }), stopped]);
        if (!current()) return;
        const fragment = new DOMParser().parseFromString(html, 'text/html');
        const rendered = fragment.getElementById(section.id);
        if (!rendered) return;
        const candidates = rendered.querySelectorAll('cpp-website-offer');
        if (!candidates.length) { this.removeOffer(); return; }
        if (candidates.length !== 1) return;
        const offer = candidates[0];
        const dates = this.offerDates(offer);
        if (offer.dataset.sectionId !== sectionId || !dates || dates.until <= Date.now()) return;
        // Reject cached/older content; a successful refresh must extend the observed metadata window.
        if (Date.parse(dates.expiry) <= Date.parse(expiry) || Date.parse(dates.expiry) > Date.now() + 300000) return;
        const p = offer.children[0];
        if (offer.children.length !== 1 || p?.tagName !== 'P' || p.attributes.length || p.childNodes.length !== 3) return;
        const [amount, join, code] = p.childNodes;
        if (amount.nodeName !== 'STRONG' || code.nodeName !== 'STRONG' || amount.attributes.length || code.attributes.length || join.nodeType !== Node.TEXT_NODE || join.textContent !== ' · Use code ') return;
        if (![amount, code].every(node => node.childNodes.length === 1 && node.firstChild.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0 && node.textContent.length <= 300)) return;
        const safeP = document.createElement('p');
        const safeAmount = document.createElement('strong'); safeAmount.textContent = amount.textContent;
        const safeCode = document.createElement('strong'); safeCode.textContent = code.textContent;
        safeP.append(safeAmount, document.createTextNode(join.textContent), safeCode);
        this.replaceChildren(safeP);
        this.dataset.expiresAt = dates.expiry;
        this.dataset.endsAt = offer.dataset.endsAt || '';
        if (dates.until - Date.now() <= 15000) this.attemptedExpiry = dates.expiry;
        this.checkExpiry();
      } catch {
        // One attempt for this expiry. Keep the original verified copy only until its original end/expiry.
      } finally {
        clearTimeout(timeout);
        if (this.flight === flight) this.flight = null;
      }
    }
    removeOffer() {
      const slide = this.closest('.swiper-slide');
      const carousel = this.closest('carousel-slider');
      if (!slide || !carousel) { this.remove(); return; }
      const config = carousel.getAttribute('data-options') || carousel.querySelector('script[ref="config"]')?.textContent;
      slide.remove();
      let options;
      try { options = JSON.parse(config); } catch { console.error('CPP_WEBSITE_OFFER_CAROUSEL_CONFIG_INVALID'); return; }
      if (!options || typeof options !== 'object' || Array.isArray(options)) { console.error('CPP_WEBSITE_OFFER_CAROUSEL_CONFIG_INVALID'); return; }
      const count = carousel.querySelectorAll('.swiper-wrapper > .swiper-slide').length;
      if (!count) { carousel.closest('.announcement-bar')?.remove(); return; }
      options.loop = count > 1;
      if (count < 2) options.autoplay = false;
      const controls = carousel.querySelector('.carousel__controls');
      if (controls) controls.hidden = count < 2;
      carousel.setAttribute('data-options', JSON.stringify(options));
    }
    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      clearTimeout(this.expiryTimer);
      this.flight?.controller.abort();
      this.flight?.cancel();
      clearTimeout(this.flight?.timeout);
      this.flight = null;
      window.removeEventListener('load', this.onPageReady);
      window.removeEventListener('pageshow', this.checkExpiry);
      document.removeEventListener('visibilitychange', this.checkExpiry);
    }
  });
}
